// src/pages/Login.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { startRouteForUserAsync } from "../utils/startRouteForUser";
import { useTranslation } from "react-i18next";

export const Login = () => {
  const { signIn, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Retour contextuel (ex. passerelle QR mobile : /login?redirect=/m/<token>).
  // On n'autorise QUE les chemins internes (anti open-redirect).
  const [searchParams] = useSearchParams();
  const rawRedirect = searchParams.get("redirect");
  const safeRedirect =
    rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//") ? rawRedirect : null;

  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Garde pour éviter la course entre useEffect et handleSubmit
  // Si handleSubmit est déjà en cours, le useEffect ne navigue pas
  const isSubmittingRef = useRef(false);

  const isEmailValid = useMemo(() => {
    if (!form.email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  }, [form.email]);

  // Vérifie si déjà connecté au chargement
  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      if (!isAuthenticated) return;
      // Si handleSubmit gère déjà la navigation, on ne double pas
      if (isSubmittingRef.current) return;

      if (isMounted) setIsRedirecting(true);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!isMounted) return;
        if (!session) {
          setIsRedirecting(false);
          return;
        }

        const { data: profiles, error: profileError } = await supabase
          .from("profiles")
          .select("role, approved, partner_type")
          .eq("id", session.user.id);

        if (!isMounted) return;

        if (profileError) {
          console.error("❌ Erreur profil:", profileError);
          setError(t("login.error.loadProfile"));
          setIsRedirecting(false);
          return;
        }

        const profile = profiles && profiles.length > 0 ? profiles[0] : null;
        if (!profile) {
          setError(t("login.error.noProfile"));
          setIsRedirecting(false);
          return;
        }

        const { data: { session: currentSession } } = await supabase.auth.getSession();
        const destination = await startRouteForUserAsync(profile, currentSession);
        if (isMounted) navigate(safeRedirect || destination, { replace: true });
      } catch (err) {
        if (!isMounted) return;
        if (err?.name === "AbortError" || err?.message?.includes("aborted")) return;
        console.error("❌ Erreur checkAuth:", err);
        setIsRedirecting(false);
      }
    };

    checkAuth();
    return () => { isMounted = false; };
  }, [isAuthenticated, navigate, t, safeRedirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsRedirecting(true);
    isSubmittingRef.current = true; // bloque le useEffect pendant ce flux

    try {
      const res = await signIn({
        email: form.email.trim(),
        password: form.password,
      });
      if (res?.error) throw res.error;

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        throw new Error(t("login.error.default"));
      }

      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("role, approved, partner_type")
        .eq("id", session.user.id);

      if (profileError) {
        console.error("❌ Erreur profil:", profileError);
        throw new Error(t("login.error.loadProfile"));
      }

      const profile = profiles && profiles.length > 0 ? profiles[0] : null;
      if (!profile) {
        throw new Error(t("login.error.noProfile"));
      }

      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const destination = await startRouteForUserAsync(profile, currentSession);
      navigate(safeRedirect || destination, { replace: true });
    } catch (err) {
      console.error("Erreur lors de la connexion:", err);
      setError(err?.message || t("login.error.default"));
      setIsRedirecting(false);
    } finally {
      isSubmittingRef.current = false; // libère dans tous les cas
    }
  };

  // Loader pendant les redirections
  if (isRedirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f9f7f4]">
        <div className="text-center">
          <div className="w-14 h-14 border-2 border-[#e5e0d8] border-t-[#c9a961] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#8a8378]" style={{ fontFamily: "Georgia, serif" }}>{t("login.loading")}</p>
        </div>
      </div>
    );
  }

  const inputCls =
    "w-full px-4 py-3 rounded-lg border border-[#e5e0d8] bg-white text-[#1a1a1a] placeholder-[#b8b1a4] " +
    "focus:outline-none focus:border-[#c9a961] focus:ring-2 focus:ring-[#c9a961]/30 transition-all";

  return (
    <div className="flex flex-col lg:flex-row min-h-screen" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* Colonne gauche — panneau éditorial */}
      <div className="lg:w-1/2 relative hidden lg:flex bg-[#1a1a1a] overflow-hidden">
        <img
          src={`${process.env.PUBLIC_URL}/images/ui/login_bg.png`}
          alt={t("login.hero.alt")}
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        {/* Dégradé pour la profondeur + lisibilité */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(20,18,16,0.92) 0%, rgba(20,18,16,0.55) 55%, rgba(20,18,16,0.85) 100%)" }} />
        <div className="relative z-10 flex flex-col justify-center px-14 xl:px-20 py-16 text-[#f9f7f4] max-w-xl">
          <div className="text-xs tracking-[0.35em] uppercase text-[#c9a961] mb-8">ARTKIV</div>
          <p className="text-3xl xl:text-4xl leading-[1.25] font-normal">
            {t("login.hero.copy")}
          </p>
          <div className="mt-10 h-px w-16 bg-[#c9a961]/60" />
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/explore"
              className="px-6 py-3 rounded-md bg-[#c9a961] text-[#1a1a1a] font-semibold hover:bg-[#d8bd7e] transition-colors"
            >
              {t("login.hero.exploreCta")}
            </Link>
            <Link
              to="/plans/compare-plans"
              className="px-6 py-3 rounded-md border border-[#f9f7f4]/40 text-[#f9f7f4] hover:bg-[#f9f7f4] hover:text-[#1a1a1a] transition-colors"
            >
              {t("login.hero.pricingCta")}
            </Link>
          </div>
          <p className="mt-8 text-sm text-[#f9f7f4]/60 leading-relaxed">
            {t("login.hero.proHint")}
          </p>
        </div>
      </div>

      {/* Colonne droite — formulaire */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-12 lg:p-16 bg-[#f9f7f4]">
        <div className="w-full max-w-md">
          {/* En-tête mobile */}
          <div className="lg:hidden text-center mb-10">
            <div className="text-xl tracking-[0.2em] mb-3">ARTKIV</div>
            <p className="text-sm text-[#6b6459]">{t("login.mobile.subtitle")}</p>
            <div className="mt-5 flex flex-col gap-3">
              <Link to="/explore" className="px-6 py-3 rounded-md bg-[#1a1a1a] text-[#f9f7f4] text-center font-medium hover:bg-black transition-colors">
                {t("login.mobile.explore")}
              </Link>
              <Link to="/plans/compare-plans" className="px-6 py-3 rounded-md border border-[#1a1a1a] text-[#1a1a1a] text-center font-medium hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-colors">
                {t("login.mobile.compare")}
              </Link>
            </div>
          </div>

          {/* Titre desktop */}
          <div className="hidden lg:block mb-8">
            <div className="text-xs tracking-[0.14em] uppercase text-[#8a8378] mb-3">{t("login.subtitle")}</div>
            <h2 className="text-4xl font-bold text-[#1a1a1a] leading-tight">{t("login.title")}</h2>
            <div className="mt-4 h-px w-12 bg-[#c9a961]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-wide text-[#8a8378] mb-2">{t("login.email.label")}</label>
              <input
                type="email"
                placeholder={t("login.email.placeholder")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                required
              />
              {!isEmailValid && <p className="mt-2 text-xs text-[#a86b1f]">{t("login.email.hint")}</p>}
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-xs uppercase tracking-wide text-[#8a8378]">{t("login.password.label")}</label>
                <Link to="/reset-password" className="text-xs text-[#8a6d1f] hover:underline">{t("login.forgot")}</Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder={t("login.password.placeholder")}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={inputCls + " pr-16"}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8a8378] hover:text-[#1a1a1a]"
                  aria-label={showPassword ? t("login.password.hide") : t("login.password.show")}
                >
                  {showPassword ? t("login.password.hide") : t("login.password.show")}
                </button>
              </div>
              <p className="mt-2 text-xs text-[#8a8378]">{t("login.security.hint")}</p>
            </div>

            {error && (
              <div className="bg-[#fbecec] border border-[#e7b7b7] rounded-lg p-3">
                <p className="text-[#b3261e] text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full py-3.5 rounded-lg bg-[#c9a961] text-[#1a1a1a] font-semibold hover:bg-[#d8bd7e] transition-colors"
            >
              {t("login.submit")}
            </button>

            <div className="text-center">
              <Link to="/explore" className="text-sm text-[#6b6459] hover:text-[#1a1a1a] underline underline-offset-4">
                {t("login.altCta")}
              </Link>
            </div>
          </form>

          <div className="mt-10 pt-8 border-t border-[#e5e0d8] text-center">
            <p className="text-sm text-[#6b6459] mb-4">{t("login.noAccount")}</p>
            <Link
              to="/register"
              className="block w-full py-3 rounded-lg border border-[#1a1a1a] text-[#1a1a1a] font-medium hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-all"
            >
              {t("login.create")}
            </Link>
            {/* Pré-onboarding public : aide au choix de la formule, sans compte */}
            <Link to="/orientation" className="block mt-3 text-sm hover:underline" style={{ color: "#8a6d1f" }}>
              {t("login.findPlan", { defaultValue: "Pas sûr de votre formule ? Trouver mon espace" })}
            </Link>
            <p className="mt-4 text-xs text-[#8a8378]">{t("login.softNote")}</p>
          </div>
        </div>
      </div>
    </div>
  );
};